import { marked, MarkedOptions, Token } from 'marked';
import { decodeHtmlEntities } from './HtmlEntities';

/**
 * Token alternation regex used by `wrapBareUrlsInAutolinks`.
 *
 * Each alternative either consumes a region of markdown that we MUST
 * leave alone (code, links, autolinks, HTML tags) or — as the final
 * alternative — captures a bare http(s) URL that should be wrapped.
 * JavaScript regex alternation tries alternatives left-to-right at
 * each position, so the order here matters: skip-regions come first,
 * the bare-URL fallback comes last.
 *
 * Bare-URL char class:
 *   - left edge `(^|[^\w<("'\`=[/])` — the URL must be at the start of
 *     the string or follow a character that is not URL-meaningful and
 *     does not signal that we are already inside a markdown / HTML
 *     construct that wraps the URL itself (`<` for autolinks/HTML,
 *     `(` for markdown-link parens, `"` `'` for HTML-attribute
 *     values, `=` for `href=` / `src=`, `[` for markdown-link text,
 *     `` ` `` for inline code, `/` to avoid biting into a URL
 *     fragment, and any word char to avoid matching mid-URL).
 *   - URL body `https?:\/\/[^\s<>"\'\`\[\]]+` — accepts `(` and `)`
 *     because those characters legally appear inside URLs (e.g.
 *     `https://en.wikipedia.org/wiki/Function_(mathematics)`); we
 *     balance unmatched trailing parens in the post-processing step.
 *     Excludes `[` and `]` so a URL immediately followed by a markdown
 *     annotation marker (`https://x.com/foo[^1|Title|url]`) stops at
 *     the `[` rather than eating the annotation into the autolink and
 *     causing marked to URL-encode the bracketed text into the href.
 */
const TOKEN_RE = new RegExp(
  [
    '```[\\s\\S]*?```', // fenced code block
    '`[^`\\n]+`', // inline code span
    '!\\[[^\\]]*\\]\\([^)]+\\)', // markdown image
    '\\[[^\\]]*\\]\\([^)]+\\)', // markdown link [text](url)
    '<https?:\\/\\/[^\\s>]+>', // existing CommonMark autolink
    '<\\/?[a-zA-Z][^>]*>', // HTML opening / closing tag
    '(^|[^\\w<("\'`=\\[/])(https?:\\/\\/[^\\s<>"\'`\\[\\]]+)', // bare URL fallback
  ].join('|'),
  'g',
);

/**
 * Trailing punctuation that is almost always sentence-final rather
 * than part of the URL itself. We strip it from the captured URL and
 * re-emit it after the autolink so the rendered HTML doesn't include
 * the punctuation inside the link.
 */
const TRAILING_PUNCT = new Set(['.', ',', '!', '?', ';', ':']);

/**
 * Strip URL-trailing punctuation that is almost certainly sentence
 * punctuation, plus any unbalanced closing `)` brackets. Keeps balanced
 * paren pairs (e.g. `Function_(mathematics)`) inside the autolink while
 * peeling off `(see https://example.com/foo)`-style trailing parens.
 * Square brackets are excluded from the URL body upstream so they never
 * appear here.
 */
const trimUrlTrailing = (url: string): { trimmed: string; trailing: string } => {
  let trimmed = url;
  let trailing = '';
  while (trimmed.length > 0) {
    const last = trimmed[trimmed.length - 1];
    if (TRAILING_PUNCT.has(last)) {
      trailing = last + trailing;
      trimmed = trimmed.slice(0, -1);
      continue;
    }
    if (last === ')') {
      const opens = (trimmed.match(/\(/g) || []).length;
      const closes = (trimmed.match(/\)/g) || []).length;
      if (closes > opens) {
        trailing = last + trailing;
        trimmed = trimmed.slice(0, -1);
        continue;
      }
    }
    break;
  }
  return { trimmed, trailing };
};

/**
 * Wrap any bare http(s) URL in CommonMark autolink syntax (`<URL>`)
 * unless the URL is already inside a context that protects it: a
 * fenced code block, an inline code span, a markdown link / image,
 * an existing autolink, or an HTML tag / attribute.
 *
 * Why: marked's emphasis tokenizer applies to text containing a bare
 * URL with underscores; if a paragraph happens to contain two
 * underscore-bearing URLs (e.g. an article surfacing both a Google
 * Docs export link and `tos_template.docx`), the two `_`s can pair
 * as italic markers and inject `<em>` / `</em>` into the URL text.
 * Once the rendered HTML lands in an `<a href>` the `<em>` characters
 * get percent-encoded into `%3Cem%3E` and the link 404s. Wrapping the
 * URL in `<...>` puts it into a context where CommonMark guarantees
 * no inline-emphasis interpretation. The visible output is unchanged
 * — marked already auto-links bare URLs — but the URL is preserved
 * verbatim regardless of what other markup happens to be nearby.
 */
export const wrapBareUrlsInAutolinks = (markdown: string): string => {
  if (typeof markdown !== 'string' || markdown.length === 0) {
    return markdown;
  }

  return markdown.replace(TOKEN_RE, (match, prefix?: string, url?: string) => {
    // The bare-URL alternative is the only one that produces capture
    // groups. Any other alternative leaves prefix/url undefined; we
    // return the matched region unchanged so it stays protected.
    if (typeof prefix !== 'string' || typeof url !== 'string') {
      return match;
    }
    const { trimmed, trailing } = trimUrlTrailing(url);
    return `${prefix}<${trimmed}>${trailing}`;
  });
};

/**
 * Drop-in replacement for `marked.parse` that pre-wraps bare URLs in
 * CommonMark autolinks so that no part of any URL — most importantly
 * not its `_` characters — can be misinterpreted as inline emphasis.
 *
 * Always renders synchronously: the helper passes `async: false` to
 * `marked.parse` AFTER the caller-supplied options so callers can't
 * accidentally flip async back on and turn the synchronous return
 * type into a Promise. Falls back to returning the input as-is on
 * parser errors so a bad input never takes down the calling render
 * path.
 */
export const safeMarkdownParse = (
  markdown: string,
  options?: MarkedOptions,
): string => {
  try {
    const guarded = wrapBareUrlsInAutolinks(markdown);
    const result = marked.parse(guarded, { ...options, async: false });
    return typeof result === 'string' ? result : markdown;
  } catch {
    return markdown;
  }
};

/**
 * `walkTokens` hook for Markdown the server stored HTML-escaped (Kai answers:
 * "a & b" is saved as "a &amp; b"). In prose marked keeps an existing entity,
 * so it renders as "&". Inside code spans and code blocks marked escapes every
 * "&" again, so the bubble showed "&amp;" literally. Decode the code text once;
 * marked escapes it on render, so it stays inert text.
 */
export const decodeStoredCodeEntities = (token: Token): void => {
  if (
    (token.type === 'codespan' || token.type === 'code') &&
    typeof token.text === 'string'
  ) {
    token.text = decodeHtmlEntities(token.text);
  }
};
